// The drag bar on the left edge of a side panel (the workflow editor's
// properties panel, a detail drawer). The panel sits to its right, so dragging
// left widens it. Widths stream through `onResize` while dragging and settle
// once in `onResizeEnd`; double-click resets. `useResizableWidth` holds the
// width and hands back these props.

import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

export const RESIZER_WIDTH = 12;
const KEY_STEP = 16;

export interface PanelResizerState {
  width: number;
  min: number;
  max: number;
  onResize: (width: number) => void;
  onResizeEnd: (width: number) => void;
  onReset: () => void;
}

interface Props extends PanelResizerState {
  /** What the handle resizes, for screen readers ("Resize the properties panel"). */
  label: string;
  sx?: SxProps<Theme>;
}

export default function PanelResizer({
  width,
  min,
  max,
  onResize,
  onResizeEnd,
  onReset,
  label,
  sx,
}: Props) {
  const drag = useRef<{ startX: number; startWidth: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const clamp = (w: number) => Math.round(Math.min(max, Math.max(min, w)));

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startWidth: width, width };
    setDragging(true);
    document.body.style.userSelect = "none";
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const next = clamp(d.startWidth + d.startX - e.clientX);
    if (next === d.width) return;
    d.width = next;
    onResize(next);
  };
  const endDrag = () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    setDragging(false);
    document.body.style.userSelect = "";
    if (d.width !== d.startWidth) onResizeEnd(d.width);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === "ArrowLeft" ? KEY_STEP : e.key === "ArrowRight" ? -KEY_STEP : 0;
    if (!delta) return;
    e.preventDefault();
    const next = clamp(width + delta);
    if (next !== width) onResizeEnd(next);
  };

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
      onDoubleClick={onReset}
      sx={[
        {
          width: RESIZER_WIDTH,
          flexShrink: 0,
          cursor: "col-resize",
          touchAction: "none",
          display: "flex",
          justifyContent: "center",
          outline: "none",
          "&::after": {
            content: '""',
            width: 2,
            borderRadius: 1,
            bgcolor: dragging ? "primary.main" : "transparent",
            transition: "background-color 120ms",
          },
          "&:hover::after, &:focus-visible::after": { bgcolor: "primary.main" },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
